import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";

import { ROUTES } from "~/router";
import {
  Appointment,
  CANCEL_REASON_TYPES,
  CancellationReasonFormData,
  useAppointmentsListQuery,
  useEditVisitCancellationMutation,
  useListCancelReasonsQuery,
} from "~/services";
import { type ModalProps } from "~/shared.types";
import { errorToast, Modal, toast } from "~/ui";
import { getFieldErrorMessages } from "~/utils";
import { VisitCard } from "..";
import { CancellationReasonFields } from "../CancellationReason/CancellationReasonFields";
import { isWithinLateCancellationWindow } from "../CancellationReason/is-within-late-cancellation-window";
import { useCancellationReasonForm } from "../CancellationReason/use-cancellation-reason-form";

type VisitCancellation = NonNullable<Appointment["cancellation"]>;

/**
 * Owns the query and the guard, so the form below only ever mounts with the cancellation in
 * hand. That split is not cosmetic: useForm snapshots its defaultValues once, on mount, so a
 * form mounted while the data is still arriving would prefill empty and never recover.
 */
export const EditCancellationReasonModal = ({ show, onClose }: ModalProps) => {
  const { userId, ehrAppointmentId } = useParams<{
    userId: string;
    ehrAppointmentId: string;
  }>();

  // This route is reachable by a pasted URL and survives a refresh, neither of which passes
  // through the "Load visits" button that normally fills the cache.
  const {
    data: appointments,
    isLoading,
    isError,
    refetch: refetchAppointments,
  } = useAppointmentsListQuery(userId, { fetchIfMissing: true });

  const visit = appointments?.appointmentsHistory.find(
    (appointment) => appointment.id === ehrAppointmentId,
  );

  const cancellation = visit?.cancellation;

  // The redirect below cannot tell "no such visit" from "the request failed", and a failed
  // query is otherwise silent: the global handler only logs, and only in local. In an effect
  // rather than inline because StrictMode renders twice, which would double the toast.
  useEffect(() => {
    if (isError) {
      errorToast("We could not load this visit. Please try again.");
    }
  }, [isError]);

  // isLoading stays true across the retries, so this covers the whole pending window.
  if (isLoading) return null;

  if (isError || !ehrAppointmentId || !visit || !cancellation)
    return <Navigate to={ROUTES.dashboard} />;

  return (
    <EditCancellationReasonForm
      visit={visit}
      cancellation={cancellation}
      ehrAppointmentId={ehrAppointmentId}
      onSaved={refetchAppointments}
      show={show}
      onClose={onClose}
    />
  );
};

interface EditCancellationReasonFormProps extends ModalProps {
  visit: Appointment;
  cancellation: VisitCancellation;
  ehrAppointmentId: string;
  onSaved: () => void;
}

const EditCancellationReasonForm = ({
  visit,
  cancellation,
  ehrAppointmentId,
  onSaved,
  show,
  onClose,
}: EditCancellationReasonFormProps) => {
  const { data: cancelReasonsList } = useListCancelReasonsQuery(
    CANCEL_REASON_TYPES.INTERNAL,
  );
  const { mutate: editCancellation, isPending: isEditing } =
    useEditVisitCancellationMutation();

  // Replayed from the ORIGINAL cancellation instant, not from now: by the time an edit
  // happens the visit has usually started, so measuring against now would always report that
  // no fee applies and the card would never show. A missing instant means the rule cannot be
  // evaluated, which the server also treats as not applicable.
  const isFeeWindowOpen = Boolean(
    cancellation.cancelled_at &&
    isWithinLateCancellationWindow(
      visit.date,
      new Date(cancellation.cancelled_at),
    ),
  );

  const {
    form,
    values,
    subReasons,
    showFeeWaiver,
    handleFieldChange,
    handleParentChange,
    validateSubReason,
    setWaiveFee,
  } = useCancellationReasonForm({
    reasons: cancelReasonsList,
    defaultValues: {
      cancelReasonParentId:
        // The stored reason is the leaf when it has a parent, so the parent dropdown gets the
        // parent id and the sub-reason dropdown the leaf. When there is no parent the stored
        // reason IS the top level selection.
        (
          cancellation.parent_cancel_reason_id ??
          cancellation.appointment_cancel_reason_id
        )?.toString() ?? "",
      cancelReasonChildId: cancellation.parent_cancel_reason_id
        ? (cancellation.appointment_cancel_reason_id?.toString() ?? undefined)
        : undefined,
      cancellationNotes: cancellation.cancellation_note ?? "",
      waiveFee: cancellation.late_cancellation_fee_waived ?? false,
    },
    isFeeWindowOpen,
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = (formData: CancellationReasonFormData) => {
    editCancellation(
      {
        ehrAppointmentId,
        cancelReasonParentId: formData.cancelReasonParentId,
        cancelReasonChildId: formData.cancelReasonChildId,
        cancellationNotes: formData.cancellationNotes,
        waiveFee: showFeeWaiver ? formData.waiveFee : undefined,
      },
      {
        onSuccess: (result) => {
          // The local edit is saved either way, so a failed sync is a warning and not an
          // error: telling the admin it failed outright would be wrong.
          if (result.athena_sync_failed) {
            toast({
              type: "warning",
              title: "Edit saved, but Athena sync failed.",
              description:
                "The correction is stored in Elektra. Athena still shows the previous reason.",
            });
          } else {
            toast({
              type: "success",
              title: "Cancellation reason updated",
            });
          }

          onSaved();
          onClose();
        },
        onError: (error) => {
          errorToast(getFieldErrorMessages(error));
        },
      },
    );
  };

  const handleConfirm = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <Modal
      show={show}
      title="Edit cancellation reason"
      onClose={onClose}
      handleConfirm={handleConfirm}
      closeLabel="Back"
      confirmLabel="Confirm"
      isLoading={isEditing}
    >
      <form className="flex h-full flex-col gap-6">
        <VisitCard key={ehrAppointmentId} visit={visit} />

        <CancellationReasonFields
          reasons={cancelReasonsList}
          subReasons={subReasons}
          parentId={values.cancelReasonParentId || ""}
          childId={values.cancelReasonChildId || ""}
          notes={values.cancellationNotes || ""}
          waiveFee={values.waiveFee ?? false}
          showFeeWaiver={showFeeWaiver}
          reasonEditable={cancellation.reason_editable}
          reasonName={cancellation.cancel_reason_name}
          errors={{
            parentId: errors.cancelReasonParentId?.message,
            childId: errors.cancelReasonChildId?.message,
            notes: errors.cancellationNotes?.message,
          }}
          onParentChange={handleParentChange}
          onChildChange={(value) =>
            handleFieldChange("cancelReasonChildId", value)
          }
          onChildBlur={validateSubReason}
          onNotesChange={(value) =>
            handleFieldChange("cancellationNotes", value)
          }
          onWaiveFeeChange={setWaiveFee}
        />
      </form>
    </Modal>
  );
};
